import React, { useState, useContext, useEffect } from 'react';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import ToolTipWrapper from '../Tooltip/ToolTipWrapper';
import SelectUserGroupsAndUsers from './SelectUserGroupsAndUsers';

import DAndD from './DragAndDrop';
import AutoComplete from '../global/AutoComplete';

import { LockerManagementFileUploadContext } from '@/context/LockerManagementFileUploadContext';
import { IHttpRequest } from  '@/interfaces/IHttp';
import { container, TYPES } from "@/inversifyconfig/container.config";
import { LockerEvent, LockerContextRefreshType as Table } from  '@/interfaces/LockerManagement';
import { ToastMessageType } from '../global/ToastNotification';
import { Navigate } from 'react-router'
import { LockerManagementContext } from '@/context/LockerManagmentContext';



const FileUpload = ({setToast}: {setToast: (messageType: ToastMessageType, message: JSX.Element) => void}) => {

    const [uploadValidation, setUploadValidation] = useState<{maxFileSize: number, extensions: string[]}>({maxFileSize: 104857600, extensions: []})
    const [isNewBatch, setIsNewBatch] = useState<boolean>(true);
    const [autoCompleteOptions, setAutocompleteOptions] = useState<string[]>([]);
    const [autoCompleteLoading, setAutoCompleteLoading] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [hasSubmitSuccessfully, setHasSubmitSuccessfully] = useState<boolean>(false);

    const { batchName, files, setFiles, isValid, setBatchName, setFileErrors, selectedUsers, selectedGroups, clearAll} = useContext(LockerManagementFileUploadContext);
    const { refreshData } = useContext(LockerManagementContext);
    

    const httpCall = container.get<IHttpRequest>(TYPES.IHttpRequest);

    useEffect(()=> {
        httpCall.get('locker/admin/rules')
            .then(({data}:{data:{extensions: string[]}}) => {
                setUploadValidation((current) => {
                    return {
                        maxFileSize: current.maxFileSize,
                        extensions: data.extensions
                    }
                })
            })
            .catch((err) => {
            })
    },[])

    useEffect(() => {
        autoCompleteOptions.filter(name => name === batchName).length
            ? setIsNewBatch(false)
            : setIsNewBatch(true);
    }, [autoCompleteOptions, batchName])

    useEffect(() => {
        return () => { setHasSubmitSuccessfully(false); }
    }, [hasSubmitSuccessfully])

    function onSubmit(): void {

        const options = {
            headers: {
            'Content-Type': 'multipart/form-data'
            }
        } 

        setIsLoading(true);
        const formData = new FormData();
        
        files.forEach((file) => {
            formData.append(`files`, file, file.name);
        });

        selectedUsers.forEach((user, idx) => {
            formData.append(`userAliases[${idx}]`, user.alias);
        });
        
        selectedGroups.forEach((user, idx) => {
            formData.append(`groupAliases[${idx}]`, user.alias);
        });

        formData.append('batchName',  batchName)
        httpCall.post('locker/admin/files/upload', formData, options)
        .then(() => {
            setToast(ToastMessageType.SUCCESS, <>Successfully uploaded batch: <strong> {batchName} </strong></>)
            setHasSubmitSuccessfully(true)
        })
        .catch((err) => {
            setToast(ToastMessageType.WARNING, <>Failed to upload batch: <strong> {batchName} </strong></>)
        })
        .finally(()=> {
            setIsLoading(false);
            refreshData(Table.EVENTS);
        })
    }

    function getNewOptions(searchTerm) {
        setAutoCompleteLoading(true);
        
        httpCall.get('locker/batches/events', {params: {searchTerm}})
            .then(({data:{batches}}:{data: {batches: LockerEvent[]}}) => {
                setAutocompleteOptions((batches.map((event) => event.lockerBatchName)));
            })
            .catch(()=> {
                setAutocompleteOptions([]);
            })
            .finally(() => setAutoCompleteLoading(false));
    }

    function getBatchNameMessage() {
        if (!batchName) {
            return ''
        }
        else if (batchName.length > 256) {
            return 'Batch name cannot exceed 256 characters'
        }
        else if (isNewBatch){
            return `A new batch called ${batchName} will be created`
        }
        else {
            return `Files will be added to ${batchName}`
        }
    }

    if(hasSubmitSuccessfully) {
        clearAll();
        return <Navigate to="/EventOverview" />
    }

    return (
        <>
            <DAndD initialFiles={files} setFiles={(file: File[]) => setFiles(file)} setFileErrors={setFileErrors} uploadValidation={uploadValidation} setToast={setToast}/>
            
            <section className="batch-id">
                <div className="batch-id-header">
                    <label htmlFor="batch-name-input">CHOOSE EXISTING BATCH, OR CREATE NEW BATCH</label>
                    <ToolTipWrapper id="highlight-tooltip-batch-id" className="tracks-side-tooltip">
                        <ReactTooltip id="highlight-tooltip-batch-id" place="top" className='thrive-tooltip' delayHide={1000}>
                            <span>Either Select an existing batch from the list, or create a new batch by adding a new name.</span>
                        </ReactTooltip>
                    </ToolTipWrapper>
                </div>
                <AutoComplete
                    initialValue={batchName}
                    setValue={setBatchName}
                    options={autoCompleteOptions}
                    updateOptions={getNewOptions}
                    isLoading={autoCompleteLoading}
                />
                <div style={{minHeight: '17px', color: '#6a6f75', wordBreak: 'break-word'}}>
                    {getBatchNameMessage()}
                </div>
               
            </section>
            <SelectUserGroupsAndUsers />
            <div className="submit-button-wrapper">
                <button className='submit-file-upload-button' disabled={!(isValid && !!batchName && batchName.length <= 256) || isLoading} onClick={onSubmit} >
                    {!isLoading ? <>Upload File(s) - Create Batch</> : <>Uploading Files</>}
                </button>
                <button className='submit-file-upload-button cancel' onClick={clearAll}>Cancel</button>
            </div>
        </>
    )
}

export default FileUpload;